import CachedIcon from "@mui/icons-material/Cached";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  CircularProgress,
  Fab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import DualListWithLinks, {
  DualListLeftItem,
} from "components/DualListWithLinks";
import RedirectBackButton from "components/RedirectBackButton";
import RowElement from "components/RowElement";
import getUserDisplayName from "helpers/userHelper";
import useGlobalContext from "hooks/globalData";
import useTitle from "hooks/useTitle";
import UserSkuAuditTimelineTable from "pages/UserListPage/UserSkuAuditTimelineTable";
import { useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import {
  ActiveExamRequest,
  OrganizationUserDetails,
  ResponseOfUserSkuExtended,
} from "services/KnowfullyAdminClient";
import { SkuAvailabilities, UserSkuStates } from "types/common-types";
import { convertToZonedDate } from "utils/dateUtils";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

const UserCoursePage = () => {
  const { formatMessage: t } = useIntl();
  const {
    organizationUserService,
    userSkuService,
    userEvaluationService,
    userDetailsUrl,
    skuDetailsUrl,
    userSkuCertificatesToolUrl,
    examResultsUrl,
    certifyUserCourseUrl,
    modifyEnrollmentUrl,
    userSkuDeleteUrl,
    processChargebackUrl,
    markUserAsAttendedUrl,
    changeParentUserSkuUrl,
  } = useGlobalContext();

  const queryClient = useQueryClient();

  const { userId, userSkuId } = useParams<{
    userId: string;
    userSkuId: string;
  }>();

  const userResult = useQuery<OrganizationUserDetails>(
    ["getOrganizationUserDetails", userId],
    () => organizationUserService.getOrganizationUserDetails(userId!),
    {
      enabled: !!userId,
    },
  );

  const user = userResult?.data;
  const userName = getUserDisplayName(user, "");
  useTitle(t({ id: "userSkuTitle" }, { name: userName }));

  const userSkuResult = useQuery<ResponseOfUserSkuExtended>(
    ["getUserSku", userSkuId],
    () =>
      userSkuService.getUserSku(
        user?.id,
        user?.organizationId,
        userSkuId!,
      ),
    { keepPreviousData: true, enabled: !!userResult && !userResult.isFetching },
  );

  const userSku = userSkuResult?.data?.data;

  const examResult = useQuery(
    ["activeExamForSku", user?.organizationId, userSku?.skuId, user?.id],
    () =>
      userEvaluationService.getActiveExamForSku(
        ActiveExamRequest.fromJS({
          userId: user?.id,
          skuId: userSku?.skuId,
          organizationId: user?.organizationId,
        }),
      ),
    {
      enabled: userResult.isFetched && userSkuResult.isFetched,
    },
  );

  const hasExam = !!examResult?.data?.data;

  const refresh = () => {
    queryClient.invalidateQueries(["getUserSku", userSkuId]);
    queryClient.invalidateQueries(["userSkuService.getUserSkuAuditTimeline"]);
    queryClient.invalidateQueries(["sku", userSku?.skuId]);
    queryClient.invalidateQueries(["getOrganizationUserDetails", userId]);
  };

  const rows = [
    RowElement("id", userSku?.id ?? "", userSkuResult.isLoading),
    RowElement(
      "skuLabel",
      userSku?.sku ?? "",
      userSkuResult.isLoading,
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        {userSku?.sku}
        <OpenInNewIcon
          fontSize="small"
          sx={{ cursor: "pointer" }}
          onClick={() => window.open(skuDetailsUrl(userSku?.skuId))}
        />
      </Box>,
    ),
    RowElement(
      "startDateLabel",
      userSku?.startDate ? convertToZonedDate(userSku?.startDate)! : "",
      userSkuResult.isLoading,
    ),
    RowElement(
      "endDateLabel",
      userSku?.endDate ? convertToZonedDate(userSku?.endDate)! : "",
      userSkuResult.isLoading,
    ),
    RowElement(
      "statusLabel",
      userSku?.userSkuStateName ?? "",
      userSkuResult.isLoading,
    ),
  ].filter((r) => !!r.component);

  const isCertificationDisabled =
    userSku?.userSkuStateName !== UserSkuStates.Active &&
    userSku?.userSkuStateName !== UserSkuStates.InProgress;

  const isModifyRegistrationDisabled =
    userSku?.userSkuStateName !== UserSkuStates.Active &&
    userSku?.userSkuStateName !== UserSkuStates.InProgress &&
    userSku?.userSkuStateName !== UserSkuStates.Expired;

  const tools: DualListLeftItem[] = [
    {
      text: t({ id: "registrationManagement" }),
      rightListItems: [
        {
          text: t({ id: "certifyCourseLabel" }),
          href: isCertificationDisabled
            ? undefined
            : certifyUserCourseUrl(userId, userSkuId),
          disabled: isCertificationDisabled,
          tooltip: t({ id: "isCertificationDisabledTooltip" }),
        },
        {
          text: t({ id: "viewModifyRegistration" }),
          href: isModifyRegistrationDisabled
            ? undefined
            : modifyEnrollmentUrl(userId, userSkuId),
          disabled: isModifyRegistrationDisabled,
          tooltip: t({ id: "isModifyRegistrationDisabledTooltip" }),
        },
        {
          text: t({ id: "deleteRegistration" }),
          href: userSkuDeleteUrl(userId, userSkuId),
        },
        {
          text: t({ id: "processChargeBackLabel" }),
          href: processChargebackUrl(userId, userSkuId),
        },
      ],
    },
    {
      text: t({ id: "preEvaluation" }),
      rightListItems: [
        // to be added in a separate ticket
      ],
    },
    ...(userSku?.skuAvailability === SkuAvailabilities.Live
      ? [
          {
            text: t({ id: "liveEvent" }),
            rightListItems: [
              {
                text: t({ id: "markAsAttendedMessage" }),
                href: markUserAsAttendedUrl(userId, userSkuId),
              },
            ],
          },
        ]
      : []),
    {
      text: t({ id: "exam" }),
      rightListItems: [
        {
          text: t({ id: "examResults" }),
          href: examResultsUrl(userId, userSkuId),
        },
      ],
      disabled: !hasExam,
    },
    {
      text: t({ id: "postEvaluation" }),
      rightListItems: [
        // to be added in a separate ticket
      ],
    },
    {
      text: t({ id: "certificates" }),
      rightListItems: [
        {
          text: t({ id: "viewModifyCerts" }),
          href: userSkuCertificatesToolUrl(userId, userSkuId),
        },
      ],
    },
    {
      text: t({ id: "creditSubmission" }),
      rightListItems: [
        // to be added in a separate ticket
      ],
    },
  ];

  const changeParentToolAvailable = !!userSku;

  if (changeParentToolAvailable) {
    const registrationManagementTool = tools.find(
      (tool) => tool.text === t({ id: "registrationManagement" }),
    );
    registrationManagementTool?.rightListItems.push({
      text: t({ id: "changeParentUserSku" }),
      href: changeParentUserSkuUrl(userId, userSkuId),
    });
  }

  return (
    <>
      {userResult.isFetching ||
        (userSkuResult.isFetching && (
          <CircularProgress size={48} style={{ margin: "20px" }} />
        ))}
      {!userResult.isFetching && !userSkuResult.isFetching && (
        <>
          <RedirectBackButton
            redirectRoute={userDetailsUrl(userId)}
            labelKey="backToDetailsButtonLabel"
          />
          <Box display="flex" alignContent="center" justifyContent="center">
            <Tooltip
              title={t({
                id: "refresh",
              })}
            >
              <Fab onClick={refresh}>
                <CachedIcon />
              </Fab>
            </Tooltip>
          </Box>

          <h1>
            <b>{userName}</b>
          </h1>
          <Box display="flex" alignItems="center" gap={2}>
            <h2>
              <b>{userSku?.title}</b>
            </h2>
            {userSku?.isTest && (
              <Chip label={t({ id: "test" })} color="warning" size="small" />
            )}
          </Box>

          <Box mb={4}>
            <TableContainer component={Paper}>
              <Table>
                <TableBody>
                  {rows.map(({ title, component }, i) => (
                    <TableRow key={i}>
                      <TableCell component="th" scope="row">
                        {title}
                      </TableCell>
                      <TableCell>{component}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Box marginTop="30px">
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>{t({ id: "audit" })}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <UserSkuAuditTimelineTable userSkuId={userSkuId!} />
              </AccordionDetails>
            </Accordion>
          </Box>

          <Box marginTop="30px">
            <h2>{t({ id: "tools" })}</h2>
            <DualListWithLinks listItems={tools} />
          </Box>
        </>
      )}
    </>
  );
};

export default UserCoursePage;
